import React, { ChangeEvent, FC, useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'
import { isEqual } from 'lodash'

import MainModal from './common/MainModal'
import { CustomScoringModalSection } from './components/Operations/CustomScoringModalSection/CustomScoringModalSection'
import { CustomScoringModalHeader } from './components/Operations/CustomScoringModalHeader/CustomScoringModalHeader'
import { FurtherRemarksInput } from './components/Operations/FurtherRemarksInput/FurtherRemarksInput'
import { ScoringRiderInfo } from './components/Operations/ScoringRiderInfo/ScoringRiderInfo'
import { FenceHeight } from './components/Operations/FenceHeight/FenceHeight'
import { JudgesSignature } from './components/Operations/JudgesSignature/JudgesSignature'

import useToasterHelper from '../../helpers/ToasterHelper'
import { generateName } from '../../helpers/helpers'
import { handleImageError } from '../../helpers/handleImageError'
import {
  sortByTotalPoints,
  sortResultsByTime,
} from '../../pages/organizer/manage/ManageClinicNOtherOperationsTab/data/operations-tab-utils'
import { getAllRegistrationFees } from '../../helpers/registrationFees'
import { httpService } from '../../services/httpService'

import FirestoreService from '../../services/firestoreService'
import RegistrationByDayService from '../../services/firebase-service/registration-by-day/RegistrationDyDayService'

import { useAppSelector } from '../../store/hooks'
import { selectProfileData } from '../../store/user/userSlice'

import {
  IScoreCardInterface,
  IScoreCardSection,
} from '../../models/score-cards/score-cards.interface'
import {
  IRegistrationByDayInterface,
  ISheetSection,
} from '../../models/registrations-by-day/registrationByDay.interface'
import { RegistrationFeesType } from '../../models/event-fees/event-fees.interface'

import { EventFeesModel } from '../../models/event-fees/event-fees.model'
import { EventReviewPublishModel } from '../../models/event-review-publish/event-review-publish.model'

import { MODAL_CONSTS } from '../../const/modal-const'
import { CONST } from '../../const/const'
import { MESSAGES_CONST } from '../../const/messages-const'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'
import { getCurrency } from '../../helpers/payment'

interface CustomScoringModalProps {
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
  dataToPassOn: {
    modalTitle?: string
    editable: boolean
    onSaveScoring?: () => Promise<void>
    rider: IRegistrationByDayInterface
    prizeMoney?: string
    publishPrize?: boolean
  }
}

export const CustomScoringModal: FC<CustomScoringModalProps> = ({ handleModal, dataToPassOn }) => {
  const { modalTitle, editable, rider } = dataToPassOn

  const profileData = useAppSelector(selectProfileData)

  const toastFunctions = useToasterHelper()

  const [file, setFile] = useState<string | null>(rider?.score?.file ?? null)
  const [row, setRow] = useState<IRegistrationByDayInterface>(rider ?? {})
  const [loading, setLoading] = useState(false)
  const [isLocked, setIsLocked] = useState(false)
  const [fee, setFee] = useState<RegistrationFeesType | null>(null)
  const [eventReviewPublish, setEventReviewPublish] = useState<IEventReviewPublish | null>(null)

  const currency = getCurrency(eventReviewPublish?.EventPaymentSettings?.currency)
  const {
    handleSubmit,
    watch,
    register,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm<IScoreCardInterface>({
    defaultValues: {
      id: '',
      name: '',
      furtherRemarks: '',
      sections: [],
      showJumpOff: false,
      scoreCardType: 'fastest',
      fenceHeight: '',
    },
    mode: 'onChange',
  })

  const onSubmit = async () => {
    setLoading(true)

    const registrationsByDay = await RegistrationByDayService.getByEventIds([
      dataToPassOn.rider.eventId ?? '',
    ])

    const uniqueRegistrationsByDay = registrationsByDay.reduce(
      (acc: IRegistrationByDayInterface[], currentItem) => {
        // Check if the riderId of the current item is already in the accumulator
        const isRiderIdExist = acc.some(
          (item) =>
            item.uuid === currentItem.uuid &&
            item.riderId === currentItem.riderId &&
            isEqual(item.horsesSelected, currentItem.horsesSelected)
        )

        // If riderId doesn't exist, add the current item to the accumulator
        if (!isRiderIdExist) {
          acc.push(currentItem)
        }
        return acc
      },
      []
    )

    let registrationsByDayUpdated: IRegistrationByDayInterface[] =
      uniqueRegistrationsByDay?.map((registrationByDay) => {
        if (registrationByDay.id === row.id) {
          let totalPoints = 0
          if (row.score?.scoreStatus !== 'Withdrew') {
            watch('sections')
              .filter((section) => section.type !== 'jump off' || watch('showJumpOff'))
              .forEach((section) => {
                totalPoints +=
                  Number(section.totalTime || 0) +
                  Number(section.totalFaultsCount || 0) +
                  Number(section.totalTimeFaults || 0)
              })
          }

          return {
            ...registrationByDay,
            score: {
              ...row.score,
              judges: [
                {
                  id: dataToPassOn.rider.score?.judges?.[0]?.id ?? '',
                  name: dataToPassOn.rider.score?.judges?.[0]?.name ?? '',
                  picture: dataToPassOn.rider.score?.judges?.[0]?.picture ?? '',
                  sections: [],
                  customSections: watch('sections'),
                  furtherRemarks: watch('furtherRemarks'),
                  showJumpOff: watch('showJumpOff'),
                  fenceHeight: watch('fenceHeight'),
                  scoreCardType: watch('scoreCardType'),
                  signatureUrl: dataToPassOn.rider.score?.judges?.[0]?.signatureUrl ?? null,
                  signatureDate: dataToPassOn.rider.score?.judges?.[0]?.signatureDate ?? null,
                },
              ],
              totalPoints,
              file: file ?? null,
            },
          }
        } else {
          return registrationByDay
        }
      }) ?? []

    let sortedRegistrations
    const scoreCardType = registrationsByDayUpdated?.[0]?.score?.judges?.[0]?.scoreCardType
    if (scoreCardType === 'fastest') {
      sortedRegistrations = sortResultsByTime(
        registrationsByDayUpdated,
        fee?.isAsc ? 'asc' : 'desc',
        'fastest'
      )
    } else if (scoreCardType === 'optimum') {
      sortedRegistrations = sortResultsByTime(
        registrationsByDayUpdated,
        fee?.isAsc ? 'asc' : 'desc',
        'optimal'
      )
    } else {
      sortedRegistrations = sortByTotalPoints(
        registrationsByDayUpdated,
        fee?.isAsc ? 'asc' : 'desc'
      )
    }

    let count = 0

    const registrationsByDayToUpdate: IRegistrationByDayInterface[] = []

    // update rank for all registrations that have the same class
    sortedRegistrations?.forEach((registrationByDay) => {
      if (dataToPassOn.rider.uuid === registrationByDay.uuid) {
        count++
        const current = {
          ...registrationByDay,
          ...(registrationByDay.score && {
            score: { ...registrationByDay.score, rank: registrationByDay.isScratched ? 0 : count },
          }),
        }
        registrationsByDayToUpdate.push(current)
        return current
      } else {
        return registrationByDay
      }
    })

    // Update firestore collection on BE by batch
    if (registrationsByDayToUpdate?.[0]) {
      await httpService({
        url: 'update_registrations_by_day',
        method: 'POST',
        data: {
          registrationsByDay: registrationsByDayToUpdate,
        },
      })
    }

    if (dataToPassOn?.onSaveScoring) await dataToPassOn.onSaveScoring()

    toastFunctions.success({ message: MESSAGES_CONST.EVENT_SCORE_UPDATED })
    setLoading(false)
    handleModal(false, MODAL_CONSTS.CUSTOM_SCORING)
  }

  useEffect(() => {
    if (rider.score) {
      let sections: ISheetSection[] | IScoreCardSection[] = []

      if (rider.score.judges?.[0]?.sections?.[0]) {
        sections = rider.score.judges?.[0]?.sections
      }

      if (rider.score.judges?.[0]?.customSections?.[0]) {
        sections = rider.score.judges?.[0]?.customSections
      }

      reset({
        name: rider.score.sheet?.name,
        furtherRemarks: rider.score.judges?.[0]?.furtherRemarks ?? '',
        sections,
        showJumpOff: rider.score.judges?.[0]?.showJumpOff ?? false,
        fenceHeight: rider.score.judges?.[0]?.fenceHeight ?? '',
        scoreCardType: rider.score.judges?.[0]?.scoreCardType ?? 'fastest',
      })
    }
  }, [rider.score])

  useEffect(() => {
    const getFee = async () => {
      if (row.eventId) {
        const publishedEventSnapShot = await FirestoreService.getItem(
          CONST.DATA.FIRESTORE.V01.COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
          row.eventId
        )
        const publishedEventInDb =
          EventReviewPublishModel.fromFirestoreDoc(publishedEventSnapShot).toObject()
        const eventFees = new EventFeesModel(publishedEventInDb?.EventFees).toFirestore()
        const registrationFees = getAllRegistrationFees(eventFees?.registrationFees)
        const currentFee = registrationFees?.find((registration) => registration?.uuid === row.uuid)
        setFee(currentFee || null)
        setEventReviewPublish(publishedEventInDb)
      }
    }
    getFee().then()
  }, [row.eventId, row.uuid])

  const showLockedModal = () => {
    handleModal(true, MODAL_CONSTS.RESULTS_LOCKED, {
      ...dataToPassOn,
      setIsLocked,
    })
  }

  useEffect(() => {
    if (Number(row.score?.totalPoints) > 0) {
      setIsLocked(true)
    }
  }, [row.score?.totalPoints])

  const onChangeFurtherRemarks = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (isLocked) showLockedModal()
    else setValue('furtherRemarks', e.target.value)
  }

  return (
    <MainModal
      title={modalTitle}
      titleClassName="font-bold"
      size="2xl"
      modalClassName="z-[50]"
      overlayClassName="z-[49]"
      className="!px-0"
      type="CUSTOM_SCORING"
      show={true}
      buttons={[
        {
          hidden: !editable,
          loading,
          label: 'Save & Lock',
          fullWidth: true,
          onClick: (e) => handleSubmit(onSubmit)(e),
        },
        {
          label: 'Cancel',
          fullWidth: true,
          className: 'outline-none',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor',
          onClick: () => handleModal(false, MODAL_CONSTS.CUSTOM_SCORING),
        },
      ]}
    >
      <div className="text-SeabiscuitDark200ThemeColor">
        {row?.id && (
          <ScoringRiderInfo
            row={row}
            setRow={setRow}
            setFile={setFile}
            file={file}
            publishPrize={dataToPassOn.publishPrize || false}
            prizeMoney={dataToPassOn.prizeMoney || 0}
            editable={dataToPassOn.editable ?? false}
            className="pb-6 border-b border-b-SeabiscuitLightThemeColorD3"
            currency={currency}
          />
        )}

        {rider.score?.judges?.[0] ? (
          <>
            <CustomScoringModalHeader
              isLocked={isLocked}
              showLockedModal={showLockedModal}
              sections={watch('sections')}
              watch={watch}
              editable={dataToPassOn.editable ?? false}
              row={row}
              setRow={setRow}
            />
            {watch('fenceHeight') && (
              <div className="pb-6 mb-6 border-b border-b-SeabiscuitLightThemeColorD3">
                <FenceHeight
                  className="py-3 px-3 bg-SeabiscuitGrayThemeColor rounded-md"
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
                  editable={false}
                />
              </div>
            )}
            {row.score?.scoreStatus !== 'Withdrew' && (
              <div className="border border-SeabiscuitLightThemeColorD3 rounded-md p-2">
                <div className="flex items-center gap-2">
                  <img
                    className="w-[45px] h-[45px] rounded-md object-cover"
                    src={rider.score?.judges?.[0]?.picture ?? ''}
                    onError={(e) =>
                      handleImageError(e, generateName(rider.score?.judges?.[0]?.name || ''))
                    }
                    alt=""
                  />
                  <div>
                    <p className="text-[14px] text-SeabiscuitDark200ThemeColor/50">Judge</p>
                    <p>{rider.score?.judges?.[0]?.name}</p>
                  </div>
                </div>
                {watch('sections')?.map((section, sectionIndex) => (
                  <CustomScoringModalSection
                    key={section.id}
                    section={section}
                    sectionIndex={sectionIndex}
                    setValue={setValue}
                    watch={watch}
                    register={register}
                    isLocked={isLocked}
                    showLockedModal={showLockedModal}
                    editable={dataToPassOn.editable ?? false}
                  />
                ))}
                {(profileData.id === rider.riderId ||
                  profileData.id === rider.userId ||
                  profileData.userType === CONST.USE_MODE.ORGANIZER) && (
                  <FurtherRemarksInput
                    editable={editable}
                    furtherRemarksValue={watch('furtherRemarks')}
                    onChangeFurtherRemarks={onChangeFurtherRemarks}
                  />
                )}
                {editable && (
                  <JudgesSignature
                    showLockedModal={showLockedModal}
                    isLocked={isLocked}
                    row={row}
                    setRow={setRow}
                    judgeIndex={0}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <p className="opacity-50 p-4 text-center">Please select a judge first</p>
        )}
      </div>
    </MainModal>
  )
}
